import { Box, Checkbox, FormControlLabel, FormGroup, Grid, Tooltip } from '@mui/material';
import { getNameWithCode } from '../../services';
import { InfoDialogIcon } from './InfoDialogIcon';
import { gridLayout } from '../SectionLayout';
import { useTranslation } from 'react-i18next';
import { OptionalControl, Price } from '..';
import {
  checkIsOptionReadonly,
  getPropertyValue,
  getLabelWithInCompleteIcon,
  getFeatureTooltip,
} from '../../services/ConfigurationDataHelperFunctions';
import { EConfigitProperty, EJustification, EVariableType } from '../../data/Constants';
import { DefaultIcon } from '../DefaultIcon';

import type { IMultiValueSelectControlProps } from '../../../types';
import type { IFeatureOrFamily } from '../../../types/IHelperTypes';

/**
 * Renders a dropdown which allows you to select more than one value.
 * Used for variables with multiple selections
 * @param {IMultiValueSelectControlProps} props the properties for the mutli select component
 * @returns {JSX.Element} the mutli select component
 */
export const MultiValueSelectControl = ({
  featureFlags,
  values,
  onChange,
  onOptionalChange,
  variable,
  disabled,
  showCode,
}: IMultiValueSelectControlProps) => {
  const { t } = useTranslation();

  const onInternalChange = (e: React.SyntheticEvent) => {
    onChange?.(e.currentTarget.getAttribute('value') ?? '');
  };

  return (
    <FormGroup className="w-100">
      <Grid container size={gridLayout.fullWidth}>
        {values?.map((v) => {
          const isPasswordProtected = getPropertyValue<boolean>(
            v,
            EConfigitProperty.IsPasswordProtected,
            EVariableType.Boolean
          );
          const readOnly =
            checkIsOptionReadonly(v) || isPasswordProtected || !!v.bundleInfo?.isBundled;
          const featureTooltip = getFeatureTooltip(v, readOnly || disabled, showCode);

          /*
            If hidden return null. Return null will not show any input control for particular variable in UI.
            It doesn't have any impact on configuration summary, price or any other functionality of application
          */
          if (v.suppressed || v.hide) {
            return null;
          }
          return (
            <Grid
              key={v.value}
              container
              className={v.incompatible ? 'incompatible' : 'compatible'}
              sx={{ alignItems: 'stretch' }}
              size={gridLayout.fullWidth}
            >
              <Grid
                container
                className="feature-label-grid"
                size={{
                  xs: gridLayout.xs.input,
                  sm: gridLayout.sm.input,
                }}
              >
                <DefaultIcon show={v?.assigned === EJustification.Default} />
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{featureTooltip}</span>}>
                  <Box sx={{ display: 'flex' }}>
                    <FormControlLabel
                      className={v.isSearchValueMatched ? 'SearchValueMatched' : ''}
                      label={getLabelWithInCompleteIcon(
                        getNameWithCode(v as IFeatureOrFamily, showCode),
                        v.complete ?? true
                      )}
                      checked={!!v.assigned}
                      value={v.value}
                      onChange={onInternalChange}
                      control={<Checkbox className="paddingTopBottom" />}
                      disabled={disabled || readOnly}
                      sx={{
                        alignItems: 'center',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Grid
                container
                sx={{ alignItems: 'center', justifyContent: 'center' }}
                size={{
                  xs: gridLayout.xs.infoIcon,
                  sm: gridLayout.sm.infoIcon,
                }}
              >
                <InfoDialogIcon variable={v as IFeatureOrFamily} />
              </Grid>
              <Grid
                size={{
                  xs: gridLayout.xs.qty,
                  sm: gridLayout.sm.qty,
                }}
              ></Grid>
              <Grid
                container
                sx={{ alignItems: 'center', justifyContent: 'center' }}
                size={{
                  xs: gridLayout.xs.optional,
                  sm: gridLayout.sm.optional,
                }}
              >
                <OptionalControl
                  featureFamily={variable}
                  onOptionalChange={onOptionalChange}
                  option={v}
                  disabled={disabled || readOnly}
                  featureFlag={featureFlags?.Optional}
                />
              </Grid>
              <Grid
                container
                sx={{ alignItems: 'right', justifyContent: 'right' }}
                size={{
                  xs: gridLayout.xs.price,
                  sm: gridLayout.sm.price,
                }}
              >
                <Box sx={{ display: { xs: 'inline', sm: 'none' }, textAlign: 'center' }}>
                  {t('labels.listPrice')}:&nbsp;
                </Box>
                <Price price={v.price} isBundled={v.bundleInfo?.isBundled} />
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </FormGroup>
  );
};
